/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import { inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { CanActivateFn, Router, createUrlTreeFromSnapshot, CanDeactivateFn } from '@angular/router';
import { User } from 'firebase/auth';
import { user } from 'rxfire/auth';
import { combineLatest, from, iif, of } from 'rxjs';
import { switchMap, tap, filter, take, map } from 'rxjs/operators';
import { AppState, AppStateUser } from 'src/app/services/app-state.service';
import { AccountCreationStore } from '../services/account-creation-store.service';
import { TenantMigrationNeeded } from '../services/tenant-migration-needed.service';
import { UserSignUpMethod } from '@touchplan/common';
import { tenantGetterService } from '@touchplan/firebase-common';
import { RallyPointService } from 'src/app/services/rally-point.service';
import { fancyDatabase } from '@touchplan/client-firebase';

export const cannotMigrateTenantsGuard: CanActivateFn = (route, state) => {
	const tenantMigrationNeeded = inject(TenantMigrationNeeded);
	return tenantMigrationNeeded.migrationPendingTenant$.pipe(map((tenant) => !!tenant));
};

export const mustMigrateToTenantGuard: CanActivateFn = (route, state) => {
	const tenantMigrationNeeded = inject(TenantMigrationNeeded);
	const router = inject(Router);
	return tenantMigrationNeeded.migrationPendingTenant$.pipe(
		map((tenant) => {
			if (tenant) {
				router.navigate(['/auth/migration']);
				return false;
			}
			return true;
		})
	);
};

/**
 * redirect back to login if you're in a "not password provider && not authenticated" state
 * loads tenant data into the store as a side effect
 * */
export const isNotSSOAndAuthGuard: CanActivateFn = (route, state) => {
	const accountCreationStore = inject(AccountCreationStore);
	const appState = inject(AppState);
	const auth = inject(Auth);
	const rallyPointService = inject(RallyPointService);

	return user(auth).pipe(
		switchMap((user) => {
			if (user) {
				return from(tenantGetterService.getTenantViaAuthId(fancyDatabase, user.tenantId)).pipe(
					tap((tenant) => accountCreationStore.setTenant(tenant)),
					map(() => user)
				);
			} else {
				return of(null as User);
			}
		}),
		switchMap((user) => {
			//emits/ waits for user data only if the user is authed and not null
			const userData$ = iif(
				() => !!user,
				appState.user.pipe(filter((user) => !!user)),
				of(null as AppStateUser)
			);

			return combineLatest([accountCreationStore.isSSO$, userData$]).pipe(
				take(1),
				switchMap(([isSSO, userData]) => {
					if (isSSO && !user) {
						return of(createUrlTreeFromSnapshot(route, ['../login'], route.queryParams));
					} else if (user && userData.method !== UserSignUpMethod.AuthenticationHandler) {
						if (isSSO && userData?.method === UserSignUpMethod.RallyPoint) {
							return accountCreationStore.accessCodeData$.pipe(
								take(1),
								switchMap((accessCodeData) => rallyPointService.testAccessCodeAndGetURLTree$(accessCodeData)),
								map((testAccessCodeResult) => {
									if (testAccessCodeResult) {
										accountCreationStore.nukeState();
										return testAccessCodeResult;
									}
									return createUrlTreeFromSnapshot(route, ['/projects']);
								})
							);
						}
						//probably don't care about the isSSO part
						return of(createUrlTreeFromSnapshot(route, ['/projects']));
					} else {
						return of(true);
					}
				})
			);
		}),
		take(1)
	);
};

export const emptyAccountCreationStoreGuard: CanDeactivateFn<void> = () => {
	const accountCreationStore = inject(AccountCreationStore);

	accountCreationStore.nukeState();
	return true;
};
